import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { replacePathParams } from 'utils/routes.utils';
import { isStructuredTextPresent } from 'utils/structured-text.utils';
import { aspectRatio } from 'utils/styles.utils';

import { EVENTS_PER_PAGE_COLLECTION } from 'constants/events.constant';
import { Routes } from 'constants/routes.constants';
import { useInfiniteEventsCollection } from 'queries/collections/useInfiniteEventsCollection';
import {
  useCollectionDetailView,
  useOriginClick,
} from 'services/datalayer/browse';
import { PageCollectionFragment } from 'types/api/dato-operations.types';

import DatoImage from 'components/@common/DatoImage';
import Description from 'components/@common/Description';
import LinkUnstyled from 'components/@common/LinkUnstyled';
import EventListItem from 'components/@events/EventListItem';
import PageBreadcrumbs from 'components/@navigation/PageBreadcrumbs';

interface Props {
  collection: PageCollectionFragment;
}

const CollectionDetailContainer = ({ collection }: Props) => {
  useCollectionDetailView({
    collectionId: collection.id,
    collectionName: collection.slug,
  });
  const handleClick = useOriginClick(collection.slug, 'Collection');
  const { events, fetchNextPage, hasNextPage, isFetching } =
    useInfiniteEventsCollection({
      collectionId: collection.id,
      eventsPerPage: EVENTS_PER_PAGE_COLLECTION,
      collectionFilters: {
        timeFilter: collection.timeFilter,
        venue: collection.venue,
      },
    });

  return (
    <>
      <PageBreadcrumbs
        items={[{ href: Routes.CollectionDetail, label: collection.title }]}
      />
      {/* Changing padding from container to have paddingX: 8px on cover image*/}
      <Container sx={{ px: { xs: 1, md: 2 } }}>
        <Box
          sx={{
            display: 'grid',

            gridTemplateAreas: {
              xs: `'cover' 'container'`,
              md: `'container . cover'`,
            },
            gridTemplateColumns: {
              xs: '1fr',
              md: '6fr 1fr 5fr',
            },
            mt: { md: 3 },
            mb: { md: 15 },
          }}
        >
          {/* Re adding paddingX: 8px (16px total) for normal container padding on other items*/}
          <Stack
            sx={{
              gridArea: 'container',
              mx: { xs: 1, md: 0 },
            }}
          >
            <Stack>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  textTransform: 'uppercase',
                  mb: { xs: 1, md: 3 },
                }}
              >
                {collection.title}
              </Typography>

              {isStructuredTextPresent(collection.description?.value) && (
                <Box
                  sx={{
                    mb: { xs: 3, md: 8 },
                  }}
                >
                  {isStructuredTextPresent(collection.description.value) && (
                    <Description description={collection.description} />
                  )}
                </Box>
              )}
            </Stack>

            {!!events?.length ? (
              <Stack
                sx={{
                  gap: 2,
                  mb: { xs: 8, md: 0 },
                }}
              >
                {events.map((event, index) => (
                  <EventListItem
                    key={event.id}
                    component={LinkUnstyled}
                    onClick={handleClick}
                    href={replacePathParams(Routes.EventDetail, {
                      slug: event.slug,
                    })}
                  >
                    <EventListItem.Cover
                      cover={event.cover}
                      priority={index < 4}
                    />
                    <EventListItem.Content>
                      <EventListItem.Title>{event.title}</EventListItem.Title>

                      <EventListItem.Date
                        startDatetime={event.startDatetime}
                        endDatetime={event.endDatetime}
                        timezone={event.timezone}
                      />
                      <EventListItem.City city={event.venue.city.name} />
                    </EventListItem.Content>
                  </EventListItem>
                ))}
              </Stack>
            ) : (
              <Stack
                sx={{
                  gap: 2,
                  pb: { xs: 2 },
                }}
              >
                <Typography variant="body1">
                  <FormattedMessage id="collection.detail.events.empty.state.title" />
                </Typography>
                <Button
                  variant="outlined"
                  color="secondary"
                  component={LinkUnstyled}
                  href={Routes.Home}
                  fullWidth
                >
                  <FormattedMessage id="error.back.home.link" />
                </Button>
              </Stack>
            )}

            {hasNextPage && (
              <Box
                sx={{
                  textAlign: 'center',
                  mt: 3,
                }}
              >
                <Button
                  onClick={() => {
                    fetchNextPage();
                  }}
                  loading={isFetching}
                  variant="outlined"
                  color="secondary"
                >
                  <FormattedMessage id="events_overview.events.load_more" />
                </Button>
              </Box>
            )}
          </Stack>

          {collection.cover && (
            <Box
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 6,
                gridArea: 'cover',
                mb: { xs: 2, md: 0 },
                ...aspectRatio(1, 1),
              }}
            >
              <DatoImage
                data={collection.cover}
                fill
                priority
                style={{ objectFit: 'cover' }}
                sizes="512px"
              />
            </Box>
          )}
        </Box>
      </Container>
    </>
  );
};

export default CollectionDetailContainer;
